import React from 'react';
import { LockIcon, PlusIcon } from '../Icons/OverviewIcons.jsx';
import './StatsOverview.css';

const StatsOverview = ({ cargos = [], cargoArea = [], selectedRows = Set() }) => {
  const stats = React.useMemo(() => {
    
    const totalVolume = cargos.reduce((sum, cargo) => 
      sum + (cargo.widthX * cargo.heightY * cargo.depthZ), 0
    );
    
    const cargoAreaVolume = cargoArea.length > 0 
      ? cargoArea[0]?.widthX * cargoArea[0]?.heightY * cargoArea[0]?.depthZ 
      : 0;
    
    const utilizationRate = cargoAreaVolume > 0 
      ? (totalVolume / cargoAreaVolume * 100) 
      : 0;

    return {
      totalVolume: Math.round(totalVolume),
      cargoAreaVolume: Math.round(cargoAreaVolume),
      utilizationRate,
      availableVolume: Math.max(0, Math.round(cargoAreaVolume - totalVolume))
    };
  }, [cargos, cargoArea, selectedRows]);

  const LockedPanel = ({ title, description, price = "Premium", type = "premium", icon: Icon }) => (
    <div className={`locked-panel ${type}`}>
      <div className="locked-header">
        <div className="locked-title-wrapper">
          {Icon && <Icon size={18} color={type === 'custom' ? '#8b5cf6' : '#f59e0b'} />}
          <h4>{title}</h4>
        </div>
        <div className={`locked-badge ${type}`}>
          <LockIcon size={14} color={type === 'custom' ? '#8b5cf6' : '#f59e0b'} />
          <span>{price}</span>
        </div>
      </div>
      <p className="locked-description">{description}</p>
      <button className={`locked-button ${type}`}>
        <LockIcon size={16} color="#fff" />
        {type === 'custom' ? 'Unlock Custom Feature' : 'Upgrade to Access'}
      </button>
    </div>
  );


  // Custom icon components (add these to your Icons file or define inline)
  const CustomIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 17L12 22L22 17" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12L12 17L22 12" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ReportIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChartIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M18 20V10M12 20V4M6 20v-6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const AlertIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const AddCustomPanel = () => (
    <div className="add-custom-panel">
      <button className="add-custom-button">
        <PlusIcon size={24} color="#8b5cf6" />
        <span className="add-custom-title">Add Custom Panel</span>
        <span className="add-custom-subtitle">Create your own analytics panel</span>
      </button>
    </div>
  );



  return (
    <div className="stats-overview-modern">

          {/* Locked Panels Section */}
          <div className="locked-panels-section">
            <h4 className="section-title-modern">Advanced Analytics</h4>
            <p className="section-subtitle">Upgrade to Premium to unlock advanced features</p>
            
            <div className="locked-panels-grid">
              <LockedPanel 
                title="Cargolist (Print Form)"
                description="Generate and export detailed cargo lists in PDF and Excel formats"
                price="Premium"
                type="premium"
              />
              
              <LockedPanel 
                title="Total Costs Analysis"
                description="Complete breakdown of all shipping and handling costs"
                price="Premium"
                type="premium"
              />
              
              <LockedPanel 
                title="Transport Costs"
                description="Detailed transport cost calculations per route and carrier"
                price="Premium"
                type="premium"
              />
              
              <LockedPanel 
                title="Storage Costs"
                description="Warehouse storage cost analysis and optimization"
                price="Premium"
                type="premium"
              />
              
              <LockedPanel 
                title="Loading Costs"
                description="Labor and equipment costs for loading operations"
                price="Premium"
                type="premium"
              />
              
              <LockedPanel 
                title="Unloading Costs"
                description="Unloading operations cost analysis and efficiency metrics"
                price="Premium"
                type="premium"
              />
            </div>
          </div>

          {/* Custom Locked Panels Section */}
          <div className="locked-panels-section custom">
            <h4 className="section-title-modern">Custom Analytics Panels</h4>
            <p className="section-subtitle">Create custom dashboards tailored to your business needs</p>
            
            <div className="locked-panels-grid">
              <LockedPanel 
                title="Custom Reports"
                description="Design and generate custom reports with your own metrics"
                price="Custom"
                type="custom"
                icon={ReportIcon}
              />
              
              <LockedPanel 
                title="Advanced Charts"
                description="Create interactive charts with custom data visualizations"
                price="Custom"
                type="custom"
                icon={ChartIcon}
              />
              
              <LockedPanel 
                title="Risk Analysis"
                description="Custom risk assessment models for cargo transportation"
                price="Custom"
                type="custom"
                icon={AlertIcon}
              />
              
              <LockedPanel 
                title="Performance Metrics"
                description="Track custom KPIs and performance indicators"
                price="Custom"
                type="custom"
                icon={ChartIcon}
              />
              
              <LockedPanel 
                title="Compliance Dashboard"
                description="Monitor regulatory compliance with custom rules"
                price="Custom"
                type="custom"
                icon={CustomIcon}
              />
              
              <AddCustomPanel />
            </div>
          </div>

    </div>
  );
};

export default StatsOverview;